// skipcq: JS-W1028
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import useBreweryBeers from '../../hooks/useBreweryBeers';
import useBreweryReviewInbox from '../../hooks/useBreweryReviewInbox';
import {
  buildReviewInboxItemKey,
  filterReviewInboxItems,
  orderReviewInboxItemsBySessionKeys,
} from '../../utils/reviewDetails/aggregateReviewInboxItems';
import {
  trackReviewDetailsClickTab,
  trackReviewDetailsEmptyState,
  trackReviewDetailsSelectItem,
  trackReviewDetailsViewItemReviews,
  trackReviewDetailsViewPage,
} from '../../utils/brewhousePortalAnalytics';
import ReviewDetailsDetailPanel from './ReviewDetailsDetailPanel';
import ReviewDetailsItemList from './ReviewDetailsItemList';
import ReviewDetailsTabs, { TAB_ALL_REVIEWS, TAB_NEW_REVIEWS } from './ReviewDetailsTabs';

function buildItemKey(item) {
  return buildReviewInboxItemKey(item);
}

function ReviewDetailsWorkspace({
  breweryId,
  breweryName,
  activeTab,
  onTabChange,
  selectedItemKey,
  onSelectItemKey,
}) {
  const { items, loading, error } = useBreweryReviewInbox({ breweryId, breweryName });
  const { beers } = useBreweryBeers(breweryId);
  const trackedPageBreweryIdRef = useRef(null);
  const trackedItemRef = useRef(null);
  const trackedEmptyStateRef = useRef(null);
  const previousTabRef = useRef(null);
  const previousBreweryIdRef = useRef(null);
  const [newTabSessionOrder, setNewTabSessionOrder] = useState(() => []);

  const beerCatalogById = useMemo(
    () => new Map(beers.map((beer) => [typeof beer.id === 'string' ? beer.id : '', beer])),
    [beers],
  );

  const newItems = useMemo(
    () => filterReviewInboxItems(items, TAB_NEW_REVIEWS),
    [items],
  );
  const allItems = useMemo(
    () => filterReviewInboxItems(items, TAB_ALL_REVIEWS),
    [items],
  );

  useEffect(() => {
    if (loading) {
      return;
    }

    const enteredNewTab = previousTabRef.current !== TAB_NEW_REVIEWS
      && activeTab === TAB_NEW_REVIEWS;
    const breweryChanged = previousBreweryIdRef.current !== breweryId;

    if (activeTab === TAB_NEW_REVIEWS) {
      if (enteredNewTab || breweryChanged) {
        setNewTabSessionOrder(
          filterReviewInboxItems(items, TAB_NEW_REVIEWS).map((item) => buildItemKey(item)),
        );
      } else {
        const currentUnseenKeys = filterReviewInboxItems(items, TAB_NEW_REVIEWS)
          .map((item) => buildItemKey(item));
        setNewTabSessionOrder((previousOrder) => {
          const orderSet = new Set(previousOrder);
          const keysToAppend = currentUnseenKeys.filter((key) => !orderSet.has(key));
          if (keysToAppend.length === 0) {
            return previousOrder;
          }
          return [...previousOrder, ...keysToAppend];
        });
      }
    }

    previousTabRef.current = activeTab;
    previousBreweryIdRef.current = breweryId;
  }, [activeTab, breweryId, items, loading]);

  const visibleItems = useMemo(() => {
    if (activeTab === TAB_ALL_REVIEWS) {
      return allItems;
    }

    return orderReviewInboxItemsBySessionKeys(items, newTabSessionOrder);
  }, [activeTab, allItems, items, newTabSessionOrder]);

  const selectedItem = useMemo(
    () => visibleItems.find((item) => buildItemKey(item) === selectedItemKey) ?? null,
    [selectedItemKey, visibleItems],
  );

  useEffect(() => {
    if (loading || !breweryId || trackedPageBreweryIdRef.current === breweryId) {
      return;
    }
    trackedPageBreweryIdRef.current = breweryId;
    trackReviewDetailsViewPage({
      breweryId,
      activeTab,
      newItemsCount: newItems.length,
      allItemsCount: allItems.length,
    });
  }, [activeTab, allItems.length, breweryId, loading, newItems.length]);

  useEffect(() => {
    if (loading) {
      return;
    }
    if (visibleItems.length === 0) {
      const emptyReason = activeTab === TAB_NEW_REVIEWS ? 'no-unseen' : 'no-reviews';
      const emptyStateKey = `${breweryId ?? ''}:${activeTab}:${emptyReason}`;
      if (selectedItemKey !== null && typeof onSelectItemKey === 'function') {
        onSelectItemKey(null);
      }
      if (trackedEmptyStateRef.current === emptyStateKey) {
        return;
      }
      trackedEmptyStateRef.current = emptyStateKey;
      if (breweryId) {
        trackReviewDetailsEmptyState({
          breweryId,
          tab: activeTab,
          reason: emptyReason,
        });
      }
      return;
    }

    trackedEmptyStateRef.current = null;

    const hasSelection = visibleItems.some((item) => buildItemKey(item) === selectedItemKey);
    if (!hasSelection && typeof onSelectItemKey === 'function') {
      onSelectItemKey(buildItemKey(visibleItems[0]));
    }
  }, [
    activeTab,
    breweryId,
    loading,
    onSelectItemKey,
    selectedItemKey,
    visibleItems,
  ]);

  useEffect(() => {
    if (!selectedItem || !breweryId) {
      return;
    }
    const itemKey = buildItemKey(selectedItem);
    if (trackedItemRef.current === itemKey) {
      return;
    }
    trackedItemRef.current = itemKey;
    trackReviewDetailsViewItemReviews({
      breweryId,
      itemType: selectedItem.itemType,
      itemId: selectedItem.itemId,
      reviewCount: selectedItem.totalCount,
      unseenCount: selectedItem.unseenCount,
      tab: activeTab,
    });
  }, [activeTab, breweryId, selectedItem]);

  const handleTabChange = useCallback((nextTab) => {
    if (typeof onTabChange === 'function') {
      onTabChange(nextTab);
    }
    trackedItemRef.current = null;
    if (breweryId) {
      trackReviewDetailsClickTab({ breweryId, tab: nextTab });
    }
  }, [breweryId, onTabChange]);

  const handleSelectItem = useCallback((item) => {
    const itemKey = buildItemKey(item);
    if (typeof onSelectItemKey === 'function') {
      onSelectItemKey(itemKey);
    }
    trackedItemRef.current = null;
    if (breweryId) {
      trackReviewDetailsSelectItem({
        breweryId,
        itemType: item.itemType,
        itemId: item.itemId,
        unseenCount: item.unseenCount,
        tab: activeTab,
      });
    }
  }, [activeTab, breweryId, onSelectItemKey]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Unable to load review details. Please try again later.
      </Alert>
    );
  }

  const emptyMessage = activeTab === TAB_NEW_REVIEWS
    ? "You're all caught up — no new reviews right now."
    : 'No reviews yet for this brewery account.';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
        minHeight: 480,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRight: { md: 1 },
          borderBottom: { xs: 1, md: 0 },
          borderColor: 'divider',
          maxHeight: { md: 640 },
          minWidth: 0,
        }}
      >
        <ReviewDetailsTabs activeTab={activeTab} onTabChange={handleTabChange} />
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {visibleItems.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {emptyMessage}
              </Typography>
            </Box>
          ) : (
            <ReviewDetailsItemList
              items={visibleItems}
              selectedItemKey={selectedItemKey}
              showUnreadChip={activeTab === TAB_NEW_REVIEWS}
              showReviewedWhenZero={activeTab === TAB_NEW_REVIEWS}
              onSelectItem={handleSelectItem}
            />
          )}
        </Box>
      </Box>
      <Box sx={{ maxHeight: { md: 640 }, overflow: 'auto' }}>
        <ReviewDetailsDetailPanel
          selectedItem={selectedItem}
          activeTab={activeTab}
          breweryId={breweryId}
          beerCatalogById={beerCatalogById}
        />
      </Box>
    </Box>
  );
}

ReviewDetailsWorkspace.propTypes = {
  breweryId: PropTypes.string,
  breweryName: PropTypes.string,
  activeTab: PropTypes.oneOf([TAB_NEW_REVIEWS, TAB_ALL_REVIEWS]).isRequired,
  onTabChange: PropTypes.func.isRequired,
  selectedItemKey: PropTypes.string,
  onSelectItemKey: PropTypes.func.isRequired,
};

export default ReviewDetailsWorkspace;
